import { Middleware, UnknownAction } from '@reduxjs/toolkit';

import {
  getUserStorageKey,
  safeLocalStorageMerge,
  safeLocalStorageRead,
} from '@/lib/storage/localStorage';
import { selectUserId } from '@/redux/user/selectors';
import { SidebarContent } from '@/shared/types/sidebar';
import { RootState } from '@/shared/types/state';

interface SidebarAction extends UnknownAction {
  type: 'sidebar/setSidebarContent' | 'sidebar/setDefaultSidebar' | 'sidebar/openSidebar';
  payload: SidebarContent | { content: SidebarContent };
}

/**
 * Helper function to check if sidebar is dismissed
 */
function isSidebarDismissed(userId: string | null): boolean {
  const key = userId ? getUserStorageKey(userId) : 'sidebarDismissed';
  const storedValue = safeLocalStorageRead(key);

  if (!storedValue) {
    return false;
  }

  try {
    const parsed = JSON.parse(storedValue);
    return parsed.sidebarDismissed === 'true';
  } catch {
    // Handle legacy format (direct string)
    return storedValue === 'true';
  }
}

/**
 * Helper function to update the sidebar dismissed state in localStorage
 */
function updateSidebarDismissedState(userId: string | null, isDismissed: string) {
  const key = userId ? getUserStorageKey(userId) : 'sidebarDismissed';
  const value = JSON.stringify({ sidebarDismissed: isDismissed });
  safeLocalStorageMerge(key, value);
}

/**
 * Sidebar middleware to handle localStorage side effects
 * This middleware listens for sidebar actions and updates localStorage accordingly
 */
export const sidebarMiddleware: Middleware<unknown, RootState> =
  (store) => (next) => (action: UnknownAction) => {
    const state = store.getState();
    const userId = selectUserId(state);

    // Handle setDefaultSidebar action with localStorage check
    if (action.type === 'sidebar/setDefaultSidebar') {
      // If sidebar is dismissed, don't proceed with the action
      if (isSidebarDismissed(userId)) {
        return action; // Return the action without processing it
      }
    }

    // Let the action pass through to the reducer
    const result = next(action);

    // Then handle side effects based on the action
    if (
      action.type === 'sidebar/setSidebarContent' ||
      action.type === 'sidebar/setDefaultSidebar' ||
      action.type === 'sidebar/openSidebar'
    ) {
      // setDefaultSidebar doesn't persist the dismissed flag (it only applies
      // a route-level default without user intent). openSidebar is always a
      // non-null open, so dismissed is implicitly 'false'.
      if (action.type === 'sidebar/setDefaultSidebar') {
        return result;
      }

      const sidebarAction = action as SidebarAction;
      const isDismissed =
        action.type === 'sidebar/setSidebarContent' && sidebarAction.payload === null
          ? 'true'
          : 'false';

      updateSidebarDismissedState(userId, isDismissed);
    }

    return result;
  };
